import { createReadableStatus } from "@app/deploy";
import type { DeployOperation, OperationStatus } from "@app/types";
import { Pill, type PillVariant } from "./pill";

export const resolveOperationStatuses = (
  stats: { status: OperationStatus; updatedAt: string }[],
): [OperationStatus, string] => {
  if (stats.length === 0) {
    return ["unknown", new Date().toISOString()];
  }
  // sort the statuses from least recent to most recent
  // this allows us to return-early with the proper time in which the states
  // were first determined
  const statuses = [...stats].sort(
    (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
  );

  let success = 0;
  for (let i = 0; i < statuses.length; i += 1) {
    const st = statuses[i];
    if (st.status === "queued") {
      return ["queued", st.updatedAt];
    }

    if (st.status === "running") {
      return ["running", st.updatedAt];
    }

    if (st.status === "failed" || st.status === "canceled") {
      return [st.status, st.updatedAt];
    }

    if (st.status === "succeeded") {
      success += 1;
    }
  }

  if (success === statuses.length) {
    if (statuses.length === 0) {
      return ["succeeded", new Date().toISOString()];
    }
    return ["succeeded", statuses[statuses.length - 1].updatedAt];
  }

  return ["unknown", new Date().toISOString()];
};

export const statusToVariant = (status: OperationStatus): PillVariant => {
  switch (status) {
    case "queued":
      return "pending";
    case "running":
      return "progress";
    case "succeeded":
      return "success";
    case "failed":
    case "canceled":
      return "error";
    default:
      return "default";
  }
};

export const OpStatus = ({ status }: Pick<DeployOperation, "status">) => {
  const str = createReadableStatus(status);
  return <Pill variant={statusToVariant(status)}>{str}</Pill>;
};
